'use client';

import { ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface HowToStep {
  text: string;
  icon?: ReactNode;
  details?: string | ReactNode;
}

export interface HowToProps {
  /**
   * The title of the how-to section
   */
  title: string;
  
  /**
   * The steps to display
   * Can be either an array of strings or an array of HowToStep objects
   * HowToStep objects can include text, an optional icon, and optional details
   * for additional explanations
   */
  steps: (string | HowToStep)[];
  
  /**
   * Optional note to display at the bottom of the steps
   */
  note?: string | ReactNode;
  
  /**
   * Optional className to apply to the container
   */
  className?: string;
}

export function HowTo({ title, steps, note, className }: HowToProps) {
  return (
    <div className={cn("space-y-4", className)}>
      <div className="prose prose-gray dark:prose-invert">
        <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
        <ol className="list-decimal ml-0 pl-5 space-y-3">
          {steps.map((step, index) => (
            <li key={index} className="pl-2 ml-2">
              {typeof step === 'string' ? (
                <span>{step}</span>
              ) : (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    {step.icon && <span className="flex-shrink-0">{step.icon}</span>}
                    <span>{step.text}</span>
                  </div>
                  {step.details && (
                    <div className="pl-6 text-sm">
                      {typeof step.details === 'string' ? (
                        <p>{step.details}</p>
                      ) : (
                        step.details
                      )}
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ol>
        {note && (
          <div className="mt-6">
            {typeof note === 'string' ? (
              <p>{note}</p>
            ) : (
              note
            )}
          </div>
        )}
      </div>
    </div>
  );
} 